
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Employee } from "@/types";
import { apiService } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

interface EmployeeFormProps {
  isOpen: boolean;
  onClose: () => void;
  employee?: Employee;
  onSubmit: (data: Partial<Employee>) => void;
}

export const EmployeeForm = ({ isOpen, onClose, employee, onSubmit }: EmployeeFormProps) => {
  const { toast } = useToast();
  const isEditing = !!employee;
  const { user } = useAuth();
  const [banks, setBanks] = useState<{ id: number; name: string }[]>([]);
  
  // Extend the local form state to include department_id and position_id
  const [formData, setFormData] = useState<Partial<Employee> & { department_id?: number; position_id?: number }>(
    employee
      ? { ...employee, department_id: employee.department_id, position_id: employee.position_id }
      : {
          firstName: "",
          lastName: "",
          otherName: "",
          email: "",
          phone: "",
          division: "",
          status: "active",
          basicSalary: 0,
          gender: "male",
          manNumber: "",
          nrc: "",
          taxId: "",
          nhimaNumber: "",
          socialSecurityNo: "",
          bankName: "",
          bankAccount: "",
          bankBranch: "",
          unionType: "",
          contributionType: "",
          department_id: undefined,
          position_id: undefined
        }
  );

  // For date fields
  const [dateOfBirth, setDateOfBirth] = useState<Date | undefined>(
    employee?.dateOfBirth ? new Date(employee.dateOfBirth) : undefined
  );
  
  // Change engagementDate to use native date input
  const [engagementDate, setEngagementDate] = useState<string>(
    employee?.engagementDate ? employee.engagementDate : ''
  );

  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    const cid = user?.cid ?? null;
    apiService.getDepartments(cid).then(data => setDepartments(data.data));
    apiService.getPositions(cid).then(data => setPositions(data.data));
    apiService.getBanks(cid).then((res) => {
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setBanks(
        list
          .filter((b: any) => b.is_active == null || Number(b.is_active) === 1)
          .map((b: any) => ({ id: Number(b.id), name: String(b.name) }))
      );
    }).catch(() => setBanks([]));
  }, [user?.cid]);

  // Update form data when employee prop changes (e.g., when full data is fetched)
  useEffect(() => {
    if (employee && isOpen) {
      setFormData({
        ...employee,
        department_id: employee.department_id,
        position_id: employee.position_id
      });
      setDateOfBirth(employee.dateOfBirth ? new Date(employee.dateOfBirth) : undefined);
      setEngagementDate(employee.engagementDate || '');
    } else if (!employee && isOpen) {
      // Reset form when adding new employee
      setFormData({
        firstName: "",
        lastName: "",
        otherName: "",
        email: "",
        phone: "",
        division: "",
        status: "active",
        basicSalary: 0,
        gender: "male",
        manNumber: "",
        nrc: "",
        taxId: "",
        nhimaNumber: "",
        socialSecurityNo: "",
        bankName: "",
        bankAccount: "",
        bankBranch: "",
        unionType: "",
        contributionType: "",
        department_id: undefined,
        position_id: undefined
      });
      setDateOfBirth(undefined);
      setEngagementDate('');
    }
  }, [employee, isOpen]);

  const filteredPositions = positions.filter(
    pos => String(pos.department_id) === String(formData.department_id)
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Fix handleNumberChange to handle mlifeContributionPercentage as well
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value === '' ? undefined : Number(value) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }
    
    // Add date fields to form data
    const updatedFormData = {
      ...formData,
      dateOfBirth: dateOfBirth ? dateOfBirth.toISOString().split('T')[0] : '',
      engagementDate: engagementDate || '',
      // Remove joinDate, ensure department_id and position_id are always included
      department_id: formData.department_id ? Number(formData.department_id) : null,
      position_id: formData.position_id ? Number(formData.position_id) : null
    };
    
    onSubmit(updatedFormData);
    toast({
      title: isEditing ? "Employee Updated" : "Employee Created",
      description: isEditing 
        ? `${formData.firstName} ${formData.lastName}'s information has been updated.`
        : `${formData.firstName} ${formData.lastName} has been added to the system.`,
    });
  };

  // Update isFormValid to only require fields that are actually present in the form and not undefined
  const isFormValid = Boolean(
    formData.firstName &&
    formData.lastName &&
    formData.email &&
    formData.department_id !== undefined &&
    formData.position_id !== undefined &&
    formData.manNumber &&
    formData.nrc &&
    formData.phone &&
    formData.gender &&
    formData.status
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Employee" : "Add New Employee"}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <Tabs defaultValue="personal" className="w-full">
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger value="personal">Personal Information</TabsTrigger>
              <TabsTrigger value="employment">Employment Details</TabsTrigger>
              <TabsTrigger value="financial">Financial Information</TabsTrigger>
            </TabsList>
            
            <TabsContent value="personal" className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name*</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="lastName">Surname*</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="otherName">Other Name</Label>
                  <Input
                    id="otherName"
                    name="otherName"
                    value={formData.otherName}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nrc">NRC*</Label>
                  <Input
                    id="nrc"
                    name="nrc"
                    value={formData.nrc}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <Input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={dateOfBirth ? dateOfBirth.toISOString().split('T')[0] : ''}
                    onChange={e => {
                      const val = e.target.value;
                      setDateOfBirth(val ? new Date(val) : undefined);
                    }}
                    className="w-full"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="taxId">TPIN</Label>
                  <Input
                    id="taxId"
                    name="taxId"
                    value={formData.taxId || ''}
                    onChange={handleChange}
                    placeholder="Tax PIN"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nhimaNumber">NHIMA Number</Label>
                  <Input
                    id="nhimaNumber"
                    name="nhimaNumber"
                    value={formData.nhimaNumber || ''}
                    onChange={handleChange}
                    placeholder="NHIMA Number"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select 
                    value={formData.gender} 
                    onValueChange={(value: "male" | "female" | "other") => handleSelectChange("gender", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address*</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="manNumber">Man Number*</Label>
                  <Input
                    id="manNumber"
                    name="manNumber"
                    value={formData.manNumber}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="employment" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="socialSecurityNo">Social Security No.</Label>
                  <Input
                    id="socialSecurityNo"
                    name="socialSecurityNo"
                    value={formData.socialSecurityNo}
                    onChange={handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="engagementDate">Engagement Date</Label>
                  <Input
                    type="date"
                    id="engagementDate"
                    name="engagementDate"
                    value={engagementDate}
                    onChange={e => setEngagementDate(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department_id">Department</Label>
                  <Select
                    value={formData.department_id ? String(formData.department_id) : ""}
                    onValueChange={value => setFormData(prev => ({
                      ...prev,
                      department_id: value ? Number(value) : undefined,
                      position_id: undefined
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(dep => (
                        <SelectItem key={dep.id} value={String(dep.id)}>{dep.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="division">Division</Label>
                  <Select 
                    value={formData.division}
                    onValueChange={(value) => handleSelectChange("division", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select division" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DIV I">DIV I</SelectItem>
                      <SelectItem value="DIV II">DIV II</SelectItem>
                      <SelectItem value="DIV III">DIV III</SelectItem>
                      <SelectItem value="DIV IV">DIV IV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
 
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="position_id">Position</Label>
                  <Select
                    value={formData.position_id ? String(formData.position_id) : ""}
                    onValueChange={value => setFormData(prev => ({
                      ...prev,
                      position_id: value ? Number(value) : undefined
                    }))}
                    disabled={!formData.department_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredPositions.map(pos => (
                        <SelectItem key={pos.id} value={String(pos.id)}>{pos.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="status">Employment Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(value: "active" | "inactive" | "on_leave") => handleSelectChange("status", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="on_leave">On Leave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="unionType">Type of Union</Label>
                  <Select 
                    value={formData.unionType} 
                    onValueChange={(value) => handleSelectChange("unionType", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select union type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ZULAWU">ZULAAWU</SelectItem>
                      <SelectItem value="FIRESUZ">FIRESUZ</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contributionType">Contribution (LF/NA/PS)</Label>
                  <Select 
                    value={formData.contributionType} 
                    onValueChange={(value) => handleSelectChange("contributionType", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select contribution type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LF">LF</SelectItem>
                      <SelectItem value="NA">NA</SelectItem>
                      <SelectItem value="PS">PS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

            </TabsContent>
            
            <TabsContent value="financial" className="space-y-4">
              
              <Separator />
              
              <div>
                <h3 className="text-sm font-medium mb-2">Banking Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bankName">Bank Name</Label>
                    <Select 
                      value={formData.bankName} 
                      onValueChange={(value) => handleSelectChange("bankName", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select bank" />
                      </SelectTrigger>
                      <SelectContent>
                        {banks.map((bank) => (
                          <SelectItem key={bank.id} value={bank.name}>
                            {bank.name}
                          </SelectItem>
                        ))}
                        {formData.bankName &&
                          !banks.some((b) => b.name === formData.bankName) && (
                            <SelectItem value={formData.bankName}>
                              {formData.bankName}
                            </SelectItem>
                          )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bankBranch">Bank Branch</Label>
                    <Input
                      id="bankBranch"
                      name="bankBranch"
                      value={formData.bankBranch}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bankAccount">Bank Account No.</Label>
                    <Input
                      id="bankAccount"
                      name="bankAccount"
                      value={formData.bankAccount}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="advanceBF">Advance Brought Forward (B/F)</Label>
                    <Input
                      id="advanceBF"
                      name="advanceBF"
                      type="number"
                      min={0}
                      step="0.01"
                      value={formData.advanceBF ?? 0}
                      onChange={handleChange}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-muted-foreground">
                      Shown on the payslip as Advance B/F and deducted once when payroll is processed.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="pt-4">
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!isFormValid}>{isEditing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
